import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Send, CheckCircle2 } from 'lucide-react';
import { api } from '@/lib/api';
import DatePicker from '@/components/ui/date-picker';
import { trackFormStart, trackFormSubmit } from '@/lib/tracker';
import { logger } from '@/lib/logger';

export const BookingDialog = ({ open, onClose, service }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', preferred_date: '', session_type_id: '', notes: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [services, setServices] = useState([]);

  useEffect(() => {
    if (!open) return;
    setForm({ name: '', email: '', phone: '', preferred_date: '', session_type_id: service?.id || '', notes: '' });
    setErrors({});
    setSuccess(false);
    trackFormStart('booking');
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    // Load services (session types)
    (async () => {
      try {
        const { data } = await api.get('/public/services');
        setServices(data || []);
      } catch (err) {
        logger.warn('Failed to load services for booking', err);
      }
    })();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, service?.id]);

  if (!open) return null;

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.session_type_id) e.session_type_id = 'Please choose a session type';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const chosen = services.find((s) => s.id === form.session_type_id);

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post('/public/bookings', {
        service_id: form.session_type_id,
        service_title: chosen?.title || service?.title || 'General Booking',
        session_type: chosen?.title || '',
        name: form.name,
        email: form.email,
        phone: form.phone,
        preferred_date: form.preferred_date,
        notes: form.notes,
      });
      trackFormSubmit('booking');
      setSuccess(true);
      toast.success('Booking request received! We\u2019ll confirm within 24 hours.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Try again in a moment.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="booking-dialog" className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#0F2E23]/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl border border-[#0F2E23]/10 bg-[#F5F0E6] shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between p-5 border-b border-[#0F2E23]/10">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#0F2E23] font-semibold">Book a session</div>
            <h3 className="mt-1 font-display text-2xl text-[#141414] leading-tight">{chosen?.title || service?.title || 'Reserve your spot'}</h3>
            {(chosen?.subtitle || service?.subtitle) && <div className="text-xs text-[#141414]/60 mt-1">{chosen?.subtitle || service?.subtitle}</div>}
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="p-2 rounded-lg hover:bg-black/5"><X className="w-4 h-4" /></button>
        </div>

        {success ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#0F2E23] text-[#F5C86A] mb-4">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h4 className="font-display text-2xl text-[#141414]">Request received</h4>
            <p className="mt-2 text-sm text-[#141414]/70 max-w-sm mx-auto">Thanks {form.name || 'so much'}! The Padel Gems team will confirm your booking within 24 hours.</p>
            <button data-testid="booking-close-success" onClick={onClose} className="pg-glossy mt-6 inline-flex items-center gap-2 bg-[#0F2E23] hover:bg-[#153F30] text-[#F5F0E6] px-5 py-2.5 rounded-xl text-[11px] tracking-[0.22em] font-semibold">Close</button>
          </div>
        ) : (
          <form data-testid="booking-form" onSubmit={onSubmit} className="p-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" error={errors.name}>
                <input data-testid="booking-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="pg-fld" placeholder="Your name" />
              </Field>
              <Field label="Email" error={errors.email}>
                <input data-testid="booking-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="pg-fld" placeholder="you@example.com" />
              </Field>
              <Field label="Phone (optional)">
                <input data-testid="booking-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="pg-fld" placeholder="+966 ..." />
              </Field>
              <Field label="Preferred date">
                <DatePicker
                  value={form.preferred_date}
                  onChange={(v) => setForm({ ...form, preferred_date: v })}
                  showTime
                  variant="public"
                  placeholder="Choose a date"
                  minDate={new Date()}
                  testId="booking-date"
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Session type" error={errors.session_type_id}>
                  <select data-testid="booking-session-select" value={form.session_type_id} onChange={(e) => setForm({ ...form, session_type_id: e.target.value })} className="pg-fld">
                    <option value="">Choose a session type</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.title}{s.default_price ? `  ·  SAR ${s.default_price}` : ''}{s.duration_min ? `  ·  ${s.duration_min} min` : ''}
                      </option>
                    ))}
                  </select>
                </Field>
                {chosen?.description && <p className="mt-2 text-xs text-[#141414]/60">{chosen.description}</p>}
              </div>
              <div className="sm:col-span-2">
                <Field label="Notes">
                  <textarea data-testid="booking-notes-textarea" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything the team should know about your level or goals?" className="pg-fld resize-none" />
                </Field>
              </div>
            </div>
            <div className="pt-2 flex items-center justify-end gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-[#141414]/15 hover:bg-black/5 text-sm">Cancel</button>
              <button data-testid="booking-submit-button" type="submit" disabled={loading} className="pg-glossy inline-flex items-center gap-2 bg-[#0F2E23] hover:bg-[#153F30] disabled:opacity-60 text-[#F5F0E6] px-5 py-2.5 rounded-xl text-[11px] tracking-[0.22em] font-semibold">
                {loading ? 'Sending…' : (<>Send request <Send className="w-4 h-4" /></>)}
              </button>
            </div>
            <style>{`.pg-fld{width:100%;border:1px solid rgba(20,20,20,.15);background:#fff;border-radius:.75rem;padding:.65rem .9rem;color:#141414;outline:none;transition:border-color .15s;}.pg-fld:focus{border-color:#0F2E23;box-shadow:0 0 0 3px rgba(15,46,35,.18);}`}</style>
          </form>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, error, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-[#141414]/60 font-semibold">{label}</span>
    <div className="mt-1">{children}</div>
    {error && <div className="mt-1 text-xs text-red-600">{error}</div>}
  </label>
);

export default BookingDialog;
